import { DragManager, type IAvatarIndexes } from './DragManager';
import { AngularInjector } from '../system';
import { type IA9GridConstants } from '../grid/constants';

export class UIListingGridV1 {
    private static gridCanvasTypes = ['.ui-grid-render-container-left', '.ui-grid-render-container-body', '.ui-grid-render-container-right'];
    private static gridLastRowClass = 'grid-last-row';
    private static gridAvatarRowClass = 'grid-avatar-row';
    public static GRID_FIRST_ROW = 0;
    public static GRID_LAST_ROW = -2;
    public static GRID_OUTSIDE = -1;

    public static getRowIndexUnderElement(
        listingGridId: string,
        elementSelector: string,
        overElement: HTMLElement,
        rowHeight: number = null
    ): number {
        let result = UIListingGridV1.GRID_OUTSIDE;
        const gridCoords = DragManager.getCoords(document.querySelector(`[id="${listingGridId}"]`));
        const canvasCoords = DragManager.getCoords(document.querySelector(`[id="${listingGridId}"] .ui-grid-canvas`));
        const innerCoords = DragManager.getCoords(overElement);
        const innerPoint = { axisX: innerCoords.left + overElement.offsetWidth / 2, axisY: innerCoords.top + overElement.offsetHeight / 2 };

        // if not on the grid => -1
        if (UIListingGridV1.isInside(gridCoords, innerPoint)) {
            if (UIListingGridV1.isHigher(canvasCoords, innerPoint)) {
                // if on the grid and higher than ui-grid-canvas => 0
                result = UIListingGridV1.GRID_FIRST_ROW;
            } else if (UIListingGridV1.isLower(canvasCoords, innerPoint)) {
                // if on the grid and lower than ui-grid-canvas => -2
                result = UIListingGridV1.GRID_LAST_ROW;
            } else {
                // if on the grid and on the ui-grid-canvas => look at the top border
                result = UIListingGridV1.getRowNumber(canvasCoords, innerPoint, rowHeight);
            }
        }
        return result;
    }

    private static isInside(parentCoords, innerPoint) {
        if (
            parentCoords.left < innerPoint.axisX &&
            parentCoords.right > innerPoint.axisX &&
            parentCoords.top < innerPoint.axisY &&
            parentCoords.bottom > innerPoint.axisY
        ) {
            return true;
        }
        return false;
    }

    private static isHigher(parentCoords, innerPoint) {
        if (parentCoords.top > innerPoint.axisY) {
            return true;
        }
        return false;
    }

    private static isLower(parentCoords, innerPoint) {
        if (parentCoords.bottom < innerPoint.axisY) {
            return true;
        }
        return false;
    }

    private static getRowNumber(parentCoords, innerPoint, rowHeight: number = null): number {
        const height = rowHeight || AngularInjector.get<IA9GridConstants>('a9GridConstants').dataRowHeight;
        return Math.floor((innerPoint.axisY - parentCoords.top) / height);
    }

    public static async moveElementTo(element, coords) {
        return new Promise((resolve, reject) => {
            const onTransitionEnd = event => {
                try {
                    resolve('');
                } catch (e) {
                    reject(e);
                }
                element.removeEventListener('transitionend', onTransitionEnd);
            };
            element.addEventListener('transitionend', onTransitionEnd);
            setTimeout(() => {
                element.style.left = `${coords.left}px`;
                element.style.top = `${coords.top}px`;
            });
        });
    }

    public static isPlaceholder(row) {
        return row.classList.contains('cloned'); // todo change
    }

    public static setPlaceholder(row) {
        const placeholder = row.cloneNode(true);
        UIListingGridV1.hoverRow(placeholder);
        placeholder.classList.add('cloned'); // todo unite
        return placeholder;
    }

    public static removeRows(rows) {
        Array.prototype.forEach.call(rows, row => {
            if (row.parentElement && row.parentElement.children.length > 1) {
                row.parentElement && row.parentElement.removeChild(row);
            }
        });
    }

    public static replaceBefore(placeholders, rows) {
        rows.forEach((row, key) => {
            row.parentElement && placeholders[key] && row.parentElement.insertBefore(placeholders[key], row);
            //row.parentElement && row.parentElement.removeChild(row);
        });
    }

    public static replaceAfter(placeholders, rows) {
        rows.forEach((row, key) => {
            row.parentElement && placeholders[key] && row.parentElement.appendChild(placeholders[key]);
            //row.parentElement && row.parentElement.removeChild(row);
        });
    }

    public static getGrid(listingGridId) {
        return <HTMLElement>document.querySelector(`[id="${listingGridId}"] .ui-grid`);
    }

    public static createAvatarElement(listingGridId: string, elementSelector: string, overElement: HTMLElement): HTMLElement {
        // todo test avatar
        const row = overElement.closest(`[id="${listingGridId}"] ${elementSelector}`);
        const grid = UIListingGridV1.getGrid(listingGridId);
        const avatar = <HTMLElement>grid.cloneNode(true);
        //let avatar = <HTMLElement>row.cloneNode(true);
        const rowIndex = UIListingGridV1.getRowId(row);
        UIListingGridV1.clearGridAvatar(avatar, elementSelector, rowIndex);
        avatar.classList.add(UIListingGridV1.gridAvatarRowClass);
        row && UIListingGridV1.positionGridAvatar(avatar, grid, row);
        if (!row || !row.nextElementSibling) {
            avatar.classList.add(UIListingGridV1.gridLastRowClass);
        }
        return avatar;
    }

    public static isLastRow(avatar) {
        return avatar && avatar.classList.contains(UIListingGridV1.gridLastRowClass);
    }

    public static getClosestRow(listingGridId: string, elementSelector: string, overElement: HTMLElement): HTMLElement {
        return <HTMLElement>overElement.closest(`[id="${listingGridId}"] ${elementSelector}`);
    }

    protected static positionGridAvatar(avatar, grid, row) {
        const top = DragManager.getCoords(row).top;
        const left = DragManager.getCoords(grid).left;
        const height = row.offsetHeight;
        avatar.style.position = 'absolute';
        avatar.style.overflow = 'hidden';
        avatar.style.zIndex = '9999';
        avatar.style.top = `${top}px`;
        avatar.style.left = `${left}px`;
        avatar.style.height = `${height}px`;
        avatar.style.border = '1px dashed #82d0f5';
    }

    public static hoverRow(row, hover = true) {
        if (hover) {
            row.querySelector('[ui-grid-row]').style.opacity = 0;
            row.style.background = '#e0f4fd';
        } else {
            row.querySelector('[ui-grid-row]').style.opacity = '';
            row.style.background = '';
        }
    }

    public static findGridRows(grid: HTMLElement, elementSelector: string, rowIndex = -1): HTMLElement[] {
        let rows = [];
        if (rowIndex === UIListingGridV1.GRID_OUTSIDE) {
            UIListingGridV1.gridCanvasTypes.forEach(type => {
                const rowsArray = grid.querySelector(`.ui-grid-contents-wrapper`).querySelectorAll(`${type} ${elementSelector}`);
                rows = rows.concat(Array.prototype.slice.call(rowsArray, 0));
            });
        } else if (rowIndex === UIListingGridV1.GRID_LAST_ROW) {
            UIListingGridV1.gridCanvasTypes.forEach(type => {
                const rowsArray = grid.querySelector(`.ui-grid-contents-wrapper`).querySelectorAll(`${type} ${elementSelector}`);
                rows = rows.concat(Array.prototype.slice.call(rowsArray, -1));
            });
        } else {
            UIListingGridV1.gridCanvasTypes.forEach(type => {
                const cell = grid.querySelector(`.ui-grid-contents-wrapper`).querySelector(`${type} *[id*='-${rowIndex}-']`);
                cell && rows.push(<HTMLElement>cell.closest(elementSelector));
            });
        }
        return rows;
    }

    public static getRowId(row: Element): number {
        let index = UIListingGridV1.GRID_OUTSIDE;
        const cell = row && row.querySelector(`*[id$='-cell']`);
        if (cell) {
            const ids = cell.id.split('-');
            if (ids.length > 0) {
                index = +ids[1];
            }
        }
        return index;
    }

    public static getRowOldId(row: Element): number {
        let index = UIListingGridV1.GRID_OUTSIDE;
        const cell = row && row.querySelector(`*[data-first-row-id]`);
        if (cell) {
            const old = cell.getAttribute('data-first-row-id');
            index = +old;
        }
        return index;
    }

    public static getRowCells(row) {
        return Array.prototype.slice.call(row.querySelectorAll(`*[id$='-cell']`), 0);
    }

    protected static clearGridAvatar(avatar, elementSelector, rowIndex) {
        // remove headers
        Array.prototype.forEach.call(avatar.querySelectorAll('.ui-grid-header,.a9-slider-controls'), header => {
            header.parentElement && header.parentElement.removeChild(header);
        });
        const removed = UIListingGridV1.findNotSelected(avatar, elementSelector, rowIndex);
        removed.forEach(rem => rem.parentElement && rem.parentElement.removeChild(rem));
    }

    protected static findNotSelected(avatar, elementSelector, rowIndex) {
        const rows = UIListingGridV1.findGridRows(avatar, elementSelector, rowIndex);
        const removed = [];
        rows.forEach(row => {
            for (let i = 0; i < row.parentElement.children.length; i++) {
                if (row.parentElement.children[i] !== row) {
                    removed.push(row.parentElement.children[i]);
                }
            }
        });
        return removed;
    }

    public static sortIndexes(grid: Element) {
        UIListingGridV1.gridCanvasTypes.forEach(type => {
            const parent = grid.querySelector(`${type} .ui-grid-canvas`);
            if (parent) {
                for (let i = 0; i < parent.children.length; i++) {
                    const child = parent.children[i];
                    const cells = child.querySelectorAll('.ui-grid-cell');
                    Array.prototype.forEach.apply(cells, [
                        cell => {
                            if (!cell.getAttribute('data-first-row-id')) {
                                const firstRowIds = cell.id.split('-');
                                cell.setAttribute('data-first-row-id', firstRowIds[1]);
                            }
                            cell.id = cell.id.replace(/-\d+-uiGrid/g, `-${i}-uiGrid`);
                        }
                    ]);
                }
            }
        });
    }

    public static resetGrid(grid: Element) {
        UIListingGridV1.gridCanvasTypes.forEach(type => {
            const parent = grid.querySelector(`${type} .ui-grid-canvas`);
            parent.innerHTML = '';
        });
    }

    public static parseElementIndexes(element): IAvatarIndexes {
        const first = UIListingGridV1.getRowOldId(element);
        const old = UIListingGridV1.getRowId(element);
        return {
            first: (first > -1 && first) || old,
            previous: old
        };
    }

    public static clearLastRow(listingGridId, elementSelector) {
        // let grid  = UIListingGridV1.getGrid(listingGridId);
        // if (grid) {
        //     let styles = document.createElement('style');
        //     styles.innerHTML = `#${listingGridId} ${elementSelector}:last-child [ui-grid-row] {opacity: 0}`;
        //     grid.insertBefore(styles, grid.firstElementChild);
        // }
    }
}
